import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";

interface Test {
    id: number;
    name: string;
}

interface Candidate {
    id: string;
    external_id: string;
    full_name: string;
    email: string;
    phone: string;
}

interface CandidateAssignment {
    id: number;
    created_utc: string;
    candidates: Candidate | null; // ✅ normalized to object or null
}

export default function AssignedCandidatesPage() {
    const [tests, setTests] = useState<Test[]>([]);
    const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
    const [selectedTestName, setSelectedTestName] = useState<string>("");
    const [assignments, setAssignments] = useState<CandidateAssignment[]>([]);
    const [loading, setLoading] = useState(false);

    // ✅ Load active tests
    useEffect(() => {
        async function loadTests() {
            const { data, error } = await supabase
                .from("tests")
                .select("id, name")
                .eq("is_active", true);

            if (error) {
                console.error("❌ Error loading tests:", error.message);
            } else {
                setTests(data || []);
            }
        }
        loadTests();
    }, []);

    // ✅ Load assignments when test selected
    useEffect(() => {
        async function loadAssignments() {
            if (!selectedTestId) return;
            setLoading(true);

            // Find test name
            const test = tests.find((t) => t.id === selectedTestId);
            setSelectedTestName(test?.name || "");

            const { data, error } = await supabase
                .from("candidateassignments")
                .select(
                    `
          id, created_utc,
          candidates!candidateassignments_candidate_id_fkey (
            id, external_id, full_name, email, phone
          )
        `
                )
                .eq("test_id", selectedTestId)
                .order("created_utc", { ascending: false });

            if (error) {
                console.error("❌ Error loading assignments:", error.message);
                setAssignments([]);
            } else {
                // ✅ Normalize: unwrap array or null
                const normalized = (data || []).map((row: any) => ({
                    id: row.id,
                    created_utc: row.created_utc,
                    candidates: Array.isArray(row.candidates)
                        ? row.candidates[0] || null
                        : row.candidates || null,
                }));
                setAssignments(normalized);
            }

            setLoading(false);
        }

        loadAssignments();
    }, [selectedTestId, tests]);

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Assigned Candidates</h1>

            {/* Test selector */}
            <div className="mb-4">
                <label className="block mb-1 font-medium">Select Test</label>
                <select
                    className="w-full border rounded px-3 py-2"
                    value={selectedTestId || ""}
                    onChange={(e) => setSelectedTestId(Number(e.target.value))}
                >
                    <option value="">-- Select a test --</option>
                    {tests.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Show selected test */}
            {selectedTestId && (
                <h2 className="text-lg font-semibold mb-4">
                    Viewing assignments for:{" "}
                    <span className="text-indigo-600">{selectedTestName}</span>
                </h2>
            )}

            {/* Assignments table */}
            {loading ? (
                <p>Loading candidates...</p>
            ) : assignments.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full border text-sm">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="p-2">Full Name</th>
                                <th className="p-2">Email</th>
                                <th className="p-2">Phone</th>
                                <th className="p-2">Assigned At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assignments.map((a) => (
                                <tr key={a.id} className="border-t">
                                    <td className="p-2">{a.candidates?.full_name || "-"}</td>
                                    <td className="p-2">{a.candidates?.email || "-"}</td>
                                    <td className="p-2">{a.candidates?.phone || "-"}</td>
                                    <td className="p-2">
                                        {new Date(a.created_utc).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                selectedTestId && (
                    <p className="text-gray-500">
                        No candidates assigned to this test yet.
                    </p>
                )
            )}
        </div>
    );
}
